// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Warble — one round of a live race, decoupled from Trystero, the DOM and the clock
// (driven by step()), which is what makes the host-transfer contract testable without a
// network (tests/takeover.test.ts).
//
// PARALLEL SAME-SEED RACE: every peer flies its OWN copy of the course — a pure function
// of the frozen seed — and reaches its OWN game-over (the ribbon ends / a Thread death)
// with no host help. NO game state ever crosses the wire — only a tiny status gossip
// (score + progress; and on finishing, accuracy + best combo) so results can show
// everyone's run. HOST TRANSFER IS A PURE DISPLAY CONCERN; onHostChange is wired anyway.

import { createGame, drainEvents, step as stepGame, type GameEvent, type GameState } from './game';
import type { Mode } from './modes';

export interface PeerResult {
  seat: number;
  name: string;
  score: number;
  progress: number; // 0..100 of the course flown
  accuracy: number; // 0..100 (only meaningful once finished)
  bestCombo: number;
  finished: boolean;
  left: boolean;
}

/** Tiny wire status. Progress rides `s`/`p`; the finish rides `d` with `a`/`c`. */
export interface RaceMsg {
  s?: number; // score
  p?: number; // progress 0..100
  a?: number; // accuracy 0..100 (on finish)
  c?: number; // best combo (on finish)
  d?: boolean; // done
}

export interface RaceDeps {
  send: (m: RaceMsg) => void;
  onChange: () => void;
  onSelfEnd: (g: GameState) => void;
}

export interface RaceOpts {
  mode: Mode;
  seed: number;
  selfSeat: number | null;
  names: string[];
  isHost: boolean;
  deps: RaceDeps;
}

export function seatOf(roster: ReadonlyArray<{ id: string }>, selfId: string): number | null {
  const i = roster.findIndex((p) => p.id === selfId);
  return i < 0 ? null : i;
}

/** A debounced one-shot: poke() ensures fn fires once, `ms` after the FIRST poke of a
 *  window; further pokes while pending do not push it back. */
export function createOneShot(ms: number, fn: () => void): { poke: () => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return {
    poke() {
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, ms);
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
}

function progressOf(s: GameState): number {
  return Math.max(0, Math.min(100, Math.round((s.clock / s.course.durMs) * 100)));
}

export class Race {
  state: GameState;
  readonly selfSeat: number | null;
  readonly mode: Mode;
  private host: boolean;
  private readonly deps: RaceDeps;
  private ended = false;
  readonly results = new Map<number, PeerResult>();

  constructor(o: RaceOpts) {
    this.mode = o.mode;
    this.state = createGame(o.mode, o.seed);
    this.selfSeat = o.selfSeat;
    this.host = o.isHost;
    this.deps = o.deps;
    o.names.forEach((name, seat) => {
      this.results.set(seat, { seat, name, score: 0, progress: 0, accuracy: 0, bestCombo: 1, finished: false, left: false });
    });
  }

  get isHost(): boolean {
    return this.host;
  }
  setHost(v: boolean): void {
    this.host = v;
  }

  private self(): PeerResult | null {
    return this.selfSeat === null ? null : (this.results.get(this.selfSeat) ?? null);
  }

  private syncSelf(): void {
    const me = this.self();
    if (!me) return;
    me.score = Math.round(this.state.score);
    me.progress = progressOf(this.state);
  }

  /** Advance the local run by `dtMs` under the current input height. Spectators no-op. */
  step(height: number, dtMs: number): GameEvent[] {
    if (this.selfSeat === null || this.state.over) return [];
    const scoreBefore = Math.round(this.state.score);
    const progBefore = progressOf(this.state);
    stepGame(this.state, height, dtMs);
    const events = drainEvents(this.state);
    this.syncSelf();
    const scoreNow = Math.round(this.state.score);
    const progNow = progressOf(this.state);
    if (scoreNow !== scoreBefore || progNow !== progBefore) {
      this.deps.send({ s: scoreNow, p: progNow });
      this.deps.onChange();
    }
    if (this.state.over && !this.ended) this.endSelf();
    return events;
  }

  private endSelf(): void {
    if (this.ended) return;
    this.ended = true;
    const sum = this.state.summary;
    const me = this.self();
    if (me && sum) {
      me.finished = true;
      me.score = Math.round(sum.score);
      me.accuracy = Math.round(sum.accuracy * 100);
      me.bestCombo = sum.bestCombo;
      me.progress = 100;
    }
    this.deps.send({ d: true, s: me?.score ?? 0, a: me?.accuracy ?? 0, c: me?.bestCombo ?? 1 });
    this.deps.onChange();
    this.deps.onSelfEnd(this.state);
  }

  /** A status update from another peer. Validated, never trusted. */
  receive(msg: RaceMsg, from: number): void {
    if (from === this.selfSeat) return; // never let the wire overwrite our own run
    const r = this.results.get(from);
    if (!r || r.left) return;
    if (typeof msg.s === 'number' && Number.isFinite(msg.s)) r.score = Math.max(0, msg.s | 0);
    if (typeof msg.p === 'number' && Number.isFinite(msg.p)) r.progress = Math.max(0, Math.min(100, msg.p | 0));
    if (typeof msg.a === 'number' && Number.isFinite(msg.a)) r.accuracy = Math.max(0, Math.min(100, msg.a | 0));
    if (typeof msg.c === 'number' && Number.isFinite(msg.c)) r.bestCombo = Math.max(1, msg.c | 0);
    if (msg.d) {
      r.finished = true;
      r.progress = 100;
    }
    this.deps.onChange();
  }

  peerLeft(seat: number): void {
    const r = this.results.get(seat);
    if (r && !r.finished) {
      r.left = true;
      this.deps.onChange();
    }
  }

  everyoneDone(): boolean {
    for (const r of this.results.values()) if (!r.finished && !r.left) return false;
    return true;
  }

  /** Highest score first; unfinished by progress; departed peers last. */
  ranked(): PeerResult[] {
    return [...this.results.values()].sort((a, b) => {
      const rank = (r: PeerResult): number => (r.left ? 2 : r.finished ? 0 : 1);
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      if (b.score !== a.score) return b.score - a.score;
      return a.seat - b.seat;
    });
  }
}
